import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import apiService from "../services/api";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import toast from "react-hot-toast";
import { format, formatDistanceToNow } from "date-fns";
import CustomerDashboard from "./CustomerDashboard";
import {
  ShoppingCart,
  DollarSign,
  Clock,
  Users,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
  TrendingUp,
  Package,
  CreditCard,
  Activity,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  UserPlus,
  ClipboardList,
  Truck,
  FileText,
  TrendingDown,
  Calendar,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingUser, setProcessingUser] = useState(null);
  const [viewUser, setViewUser] = useState(null); // For modal

  useEffect(() => {
    fetchDashboardData();
    if (isAdmin) {
      fetchPendingUsers();
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getDashboardReport();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const pending = await apiService.getPendingUsers();
      setPendingUsers(pending);
    } catch (err) {
      console.error("Failed to fetch pending users:", err);
    }
  };

  const handleUserAction = async (userId, action) => {
    setProcessingUser(userId);
    try {
      if (action === "approved") {
        await apiService.approveCustomer(userId);
        toast.success("Customer approved successfully");
      } else if (action === "rejected") {
        const reason = prompt("Please enter rejection reason:");
        if (reason) {
          await apiService.rejectCustomer(userId, reason);
          toast.success("Customer rejected successfully");
        } else {
          toast.error("Rejection reason is required");
          setProcessingUser(null);
          return;
        }
      }
      fetchPendingUsers(); // Refresh the list
      fetchDashboardData(); // Refresh dashboard stats
    } catch (err) {
      toast.error(`Failed to ${action} customer: ${err.message}`);
    } finally {
      setProcessingUser(null);
    }
  };

  // Helper to render user details in modal
  const renderUserDetails = (user) => (
    <div className="space-y-2">
      <div>
        <b>Business Name:</b> {user.businessName || "-"}
      </div>
      <div>
        <b>Business Category:</b> {user.businessCategory || "-"}
      </div>
      <div>
        <b>GST Number:</b> {user.gstNumber || "-"}
      </div>
      <div>
        <b>PAN Number:</b> {user.panNumber || "-"}
      </div>
      <div>
        <b>Contact Name:</b> {user.contactName || "-"}
      </div>
      <div>
        <b>Contact Role:</b> {user.contactRole || "-"}
      </div>
      <div>
        <b>Contact Email:</b> {user.contactEmail || "-"}
      </div>
      <div>
        <b>Contact Mobile:</b> {user.contactMobile || "-"}
      </div>
      <div>
        <b>Alternate Mobile:</b> {user.contactAltMobile || "-"}
      </div>
      <div className="mt-2 font-semibold">Bill To Address</div>
      <div>
        <b>Country:</b> {user.billCountry || "-"}
      </div>
      <div>
        <b>State:</b> {user.billState || "-"}
      </div>
      <div>
        <b>District:</b> {user.billDistrict || "-"}
      </div>
      <div>
        <b>Pin Code:</b> {user.billPinCode || "-"}
      </div>
      <div>
        <b>GPS Location:</b> {user.billGpsLocation || "-"}
      </div>
      <div>
        <b>Address:</b> {user.billAddressLine || "-"}
      </div>
      <div className="mt-2 font-semibold">Ship To Address</div>
      <div>
        <b>Country:</b> {user.shipCountry || "-"}
      </div>
      <div>
        <b>State:</b> {user.shipState || "-"}
      </div>
      <div>
        <b>District:</b> {user.shipDistrict || "-"}
      </div>
      <div>
        <b>Pin Code:</b> {user.shipPinCode || "-"}
      </div>
      <div>
        <b>GPS Location:</b> {user.shipGpsLocation || "-"}
      </div>
      <div>
        <b>Address:</b> {user.shipAddressLine || "-"}
      </div>
      <div className="mt-2 text-xs text-gray-500">
        Registered: {format(new Date(user.createdAt), "MMM dd, yyyy HH:mm")}
      </div>
    </div>
  );

  // If user is a customer, show customer dashboard
  if (!isAdmin) {
    return <CustomerDashboard />;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-28 bg-gray-200 dark:bg-gray-700 rounded-xl"
              ></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">
            Error loading dashboard
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Overview of your business performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {format(new Date(), "MMM dd, yyyy HH:mm")}
          </span>
        </div>
      </div>

      {/* Admin: Pending Registrations Section */}
      {isAdmin && pendingUsers.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-300">
                  Pending Registrations
                </h2>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {pendingUsers.length} customer
                  {pendingUsers.length > 1 ? "s" : ""} awaiting approval
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {pendingUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-800/50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-semibold text-sm">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.email}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {format(new Date(user.createdAt), "MMM dd, yyyy HH:mm")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewUser(user)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleUserAction(user.id, "approved")}
                      disabled={processingUser === user.id}
                      className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Approve"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleUserAction(user.id, "rejected")}
                      disabled={processingUser === user.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Reject"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal for viewing registration details */}
      <Modal
        isOpen={!!viewUser}
        onClose={() => setViewUser(null)}
        title="Registration Details"
        size="lg"
      >
        {viewUser && (
          <div className="space-y-6">
            {renderUserDetails(viewUser)}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={async () => {
                  await handleUserAction(viewUser.id, "approved");
                  setViewUser(null);
                }}
                disabled={processingUser === viewUser?.id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </button>
              <button
                onClick={async () => {
                  await handleUserAction(viewUser.id, "rejected");
                  setViewUser(null);
                }}
                disabled={processingUser === viewUser?.id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData?.totalOrders || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Orders
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
              <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ₹{dashboardData?.totalRevenue?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total Revenue
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ₹{dashboardData?.pendingAmount?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Pending Payments
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData?.activeCustomers || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Active Customers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin: Quick Actions */}
      {isAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/admin/customers"
              className="group flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
            >
              <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-xl group-hover:bg-blue-200 dark:group-hover:bg-blue-900 transition-colors">
                <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  Manage Customers
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Approve/reject registrations
                </p>
              </div>
            </a>

            <a
              href="/admin/orders"
              className="group flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800"
            >
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/50 rounded-xl group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900 transition-colors">
                <ClipboardList className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  Process Orders
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Review and approve orders
                </p>
              </div>
            </a>

            <a
              href="/admin/reports"
              className="group flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors border border-transparent hover:border-purple-200 dark:hover:border-purple-800"
            >
              <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-xl group-hover:bg-purple-200 dark:group-hover:bg-purple-900 transition-colors">
                <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  View Reports
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Analytics and insights
                </p>
              </div>
            </a>
          </div>
        </div>
      )}

      {/* Advanced Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend Chart - Takes 2 columns */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Revenue Trend
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Last 30 days performance
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Revenue
                  </span>
                </div>
                <div className="flex items-center gap-1.5 ml-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Orders
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-5">
            {dashboardData?.ordersTrend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData.ordersTrend}>
                  <defs>
                    <linearGradient
                      id="colorRevenue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorOrders"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    opacity={0.1}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    fontSize={12}
                    tickFormatter={(date) => format(new Date(date), "MMM dd")}
                  />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "1px solid #374151",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                    formatter={(value, name) => [
                      name === "revenue" ? `₹${value.toLocaleString()}` : value,
                      name === "revenue" ? "Revenue" : "Orders",
                    ]}
                    labelFormatter={(date) =>
                      format(new Date(date), "MMM dd, yyyy")
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorOrders)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No data available
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Revenue trends will appear here
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Order Status
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Distribution breakdown
                </p>
              </div>
            </div>
          </div>
          <div className="p-5">
            {dashboardData?.statusBreakdown?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={dashboardData.statusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="count"
                    >
                      {dashboardData.statusBreakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            [
                              "#3b82f6",
                              "#10b981",
                              "#f59e0b",
                              "#ef4444",
                              "#8b5cf6",
                              "#ec4899",
                            ][index % 6]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1f2937",
                        border: "1px solid #374151",
                        borderRadius: "8px",
                        color: "#fff",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {dashboardData.statusBreakdown.map((item, index) => (
                    <div
                      key={item.status}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: [
                              "#3b82f6",
                              "#10b981",
                              "#f59e0b",
                              "#ef4444",
                              "#8b5cf6",
                              "#ec4899",
                            ][index % 6],
                          }}
                        ></div>
                        <span className="text-gray-600 dark:text-gray-400 capitalize">
                          {item.status.replace(/_/g, " ")}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[280px]">
                <div className="text-center">
                  <Package className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No orders yet
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Performance & Payment Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg">
                <BarChart3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Top Categories
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Revenue by product category
                </p>
              </div>
            </div>
          </div>
          <div className="p-5">
            {dashboardData?.categoryData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.categoryData} layout="vertical">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    opacity={0.1}
                  />
                  <XAxis type="number" stroke="#6b7280" fontSize={12} />
                  <YAxis
                    dataKey="category"
                    type="category"
                    stroke="#6b7280"
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "1px solid #374151",
                      borderRadius: "8px",
                      color: "#fff",
                    }}
                    formatter={(value) => [
                      `₹${value.toLocaleString()}`,
                      "Revenue",
                    ]}
                  />
                  <Bar dataKey="revenue" fill="#10b981" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No category data
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                <CreditCard className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Payment Status
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Invoice payment breakdown
                </p>
              </div>
            </div>
          </div>
          <div className="p-5">
            {dashboardData?.paymentBreakdown?.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.paymentBreakdown.map((item, index) => {
                  const colors = {
                    paid: {
                      bg: "bg-emerald-100 dark:bg-emerald-900/30",
                      text: "text-emerald-600 dark:text-emerald-400",
                      bar: "bg-emerald-500",
                    },
                    unpaid: {
                      bg: "bg-red-100 dark:bg-red-900/30",
                      text: "text-red-600 dark:text-red-400",
                      bar: "bg-red-500",
                    },
                    partially_paid: {
                      bg: "bg-amber-100 dark:bg-amber-900/30",
                      text: "text-amber-600 dark:text-amber-400",
                      bar: "bg-amber-500",
                    },
                  };
                  const color = colors[item.status] || colors.unpaid;
                  const total = dashboardData.paymentBreakdown.reduce(
                    (sum, i) => sum + i.amount,
                    0
                  );
                  const percentage =
                    total > 0 ? ((item.amount / total) * 100).toFixed(0) : 0;

                  return (
                    <div key={item.status} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 ${color.bg} rounded-lg`}>
                            <FileText className={`w-3 h-3 ${color.text}`} />
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                            {item.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900 dark:text-white">
                            ₹{item.amount.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.count} invoice{item.count !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`${color.bar} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[280px]">
                <div className="text-center">
                  <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No payment data
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Section - Top Customers & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Customers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Top Customers
              </h3>
            </div>
            <a
              href="/admin/customers"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all
            </a>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {dashboardData?.topCustomers?.length > 0 ? (
                dashboardData.topCustomers.map((customer, index) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {customer.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {customer.email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        ₹{customer.totalSpent?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center justify-end gap-0.5">
                        <ArrowUpRight className="w-3 h-3" />
                        Top buyer
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No customer data available
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Customer stats will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Recent Activity
              </h3>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Live feed
            </span>
          </div>
          <div className="p-4">
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {dashboardData?.recentActivity?.length > 0 ? (
                dashboardData.recentActivity.map((activity) => {
                  const isOrder = activity.type === "order";
                  const Icon = isOrder ? Package : CreditCard;
                  const bgColor = isOrder
                    ? "bg-emerald-50 dark:bg-emerald-900/20"
                    : "bg-blue-50 dark:bg-blue-900/20";
                  const borderColor = isOrder
                    ? "border-emerald-100 dark:border-emerald-900/50"
                    : "border-blue-100 dark:border-blue-900/50";
                  const iconBg = isOrder
                    ? "bg-emerald-100 dark:bg-emerald-900/50"
                    : "bg-blue-100 dark:bg-blue-900/50";
                  const iconColor = isOrder
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-blue-600 dark:text-blue-400";

                  return (
                    <div
                      key={`${activity.type}-${activity.id}`}
                      className={`flex items-center gap-3 p-3 ${bgColor} rounded-lg border ${borderColor}`}
                    >
                      <div className={`p-2 ${iconBg} rounded-lg`}>
                        <Icon className={`w-4 h-4 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {activity.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {activity.description} • ₹
                          {activity.amount?.toLocaleString()}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        {formatDistanceToNow(new Date(activity.time), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    No recent activity
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Activity will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
